/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useState } from 'react'
import { GitCommit, AlertTriangle, X } from 'lucide-react'
import type { GitToolAction } from '@/providers/git-tools'

interface GitToolConfirmModalProps {
  action: GitToolAction
  onConfirm: (editedAction?: GitToolAction) => void
  onCancel: () => void
}

export function GitToolConfirmModal({ action, onConfirm, onCancel }: GitToolConfirmModalProps) {
  const isCreateRepo = action.type === 'create_repo'

  // Editable state for create_repo - initialize from action
  const [repoName, setRepoName] = useState(action.repoName || '')
  const [repoDescription, setRepoDescription] = useState(action.repoDescription || '')
  const [isPrivate, setIsPrivate] = useState(action.isPrivate ?? true) // Default to private

  const handleConfirm = () => {
    if (isCreateRepo) {
      // Pass edited values back
      onConfirm({
        ...action,
        repoName,
        repoDescription,
        isPrivate,
      })
    } else {
      onConfirm()
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onCancel}>
      <div
        className="bg-background-secondary border border-border rounded-lg w-full max-w-2xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            <h2 className="text-lg font-title">
              {isCreateRepo ? 'Confirm Repository Creation' : 'Confirm Git Operation'}
            </h2>
          </div>
          <button onClick={onCancel} className="text-foreground-muted hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Warning */}
        <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded">
          <p className="text-sm text-yellow-400">
            {isCreateRepo
              ? 'The AI assistant wants to create a new GitHub repository. Please review the details below before confirming.'
              : 'The AI assistant wants to commit and push changes to your GitHub repository. Please review the details below before confirming.'}
          </p>
        </div>

        {isCreateRepo ? (
          /* Repository Creation Details - Editable */
          <>
            <div className="mb-4">
              <label className="block text-xs font-semibold text-foreground-secondary mb-2">
                Repository Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={repoName}
                onChange={(e) => setRepoName(e.target.value)}
                className="w-full px-3 py-2 bg-background-tertiary border border-border rounded text-sm font-mono focus:outline-none focus:border-accent"
                placeholder="my-awesome-project"
              />
            </div>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-foreground-secondary mb-2">
                Description
              </label>
              <textarea
                value={repoDescription}
                onChange={(e) => setRepoDescription(e.target.value)}
                className="w-full px-3 py-2 bg-background-tertiary border border-border rounded text-sm focus:outline-none focus:border-accent resize-none"
                placeholder="A brief description of your project"
                rows={3}
              />
            </div>

            <div className="mb-6">
              <label className="block text-xs font-semibold text-foreground-secondary mb-2">
                Visibility
              </label>
              <div className="flex gap-3">
                <button
                  onClick={() => setIsPrivate(true)}
                  className={`flex-1 p-3 rounded border transition-all ${
                    isPrivate
                      ? 'bg-yellow-500/10 border-yellow-500/50 text-yellow-400'
                      : 'bg-background-tertiary border-border text-foreground-muted hover:border-border-hover'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <span className="text-lg">🔒</span>
                    <span className="font-semibold">Private</span>
                  </div>
                  <div className="text-xs opacity-80">Only you can see this</div>
                </button>
                <button
                  onClick={() => setIsPrivate(false)}
                  className={`flex-1 p-3 rounded border transition-all ${
                    !isPrivate
                      ? 'bg-green-500/10 border-green-500/50 text-green-400'
                      : 'bg-background-tertiary border-border text-foreground-muted hover:border-border-hover'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <span className="text-lg">🌍</span>
                    <span className="font-semibold">Public</span>
                  </div>
                  <div className="text-xs opacity-80">Anyone can see this</div>
                </button>
              </div>
            </div>
          </>
        ) : (
          /* Commit and Push Details */
          <>
            <div className="mb-4 p-3 bg-background-tertiary rounded border border-border">
              <div className="text-xs text-foreground-muted mb-1">Repository</div>
              <div className="text-sm font-mono">{action.repoUrl}</div>
              <div className="text-xs text-foreground-muted mt-2">Branch: <span className="text-accent">{action.branch}</span></div>
            </div>

            <div className="mb-4">
              <div className="text-xs font-semibold text-foreground-secondary mb-2">Commit Message:</div>
              <div className="p-3 bg-background-tertiary rounded border border-border">
                <div className="text-sm font-mono">{action.commitMessage}</div>
              </div>
            </div>

            <div className="mb-6">
              <div className="text-xs font-semibold text-foreground-secondary mb-2">
                Files to Commit ({action.files?.length || 0}):
              </div>
              <div className="max-h-48 overflow-y-auto bg-background-tertiary rounded border border-border p-2">
                {action.files?.map((file, idx) => (
                  <div key={idx} className="text-xs font-mono text-foreground-muted py-1 flex items-center gap-2">
                    <GitCommit className="w-3 h-3 text-accent flex-shrink-0" />
                    <span>{file.path}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            className="btn-secondary px-4 py-2"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isCreateRepo && !repoName.trim()}
            className="btn-primary px-4 py-2 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <GitCommit className="w-4 h-4" />
            {isCreateRepo ? 'Create Repository' : 'Confirm & Push'}
          </button>
        </div>

        {/* Safety Note */}
        <div className="mt-4 p-2 bg-background-tertiary rounded border border-border">
          <p className="text-xs text-foreground-muted">
            {isCreateRepo ? (
              <>
                <strong>Note:</strong> This will create a new repository in your GitHub account.
                {isPrivate ? ' The repository will be private.' : ' The repository will be public and visible to everyone.'}
              </>
            ) : (
              <>
                <strong>Note:</strong> This will push changes directly to the <span className="text-accent">{action.branch}</span> branch.
                Make sure you have reviewed the changes and commit message carefully.
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  )
}

